<template>
    <common-summary>
        <common-summary-item :label="$t('pages.referrals.show.details.patient_name')">
            <common-content-spacer height="tiny">
                <common-patient-name :patient="referral?.patient"></common-patient-name>

                <template v-if="referral.patient?.mobilePhone">
                    <common-icon-info icon="phone" size="medium">
                        {{ referral.patient?.mobilePhone }}
                    </common-icon-info>
                    <common-summary-item
                        :label="$t('pages.referrals.show.details.date_of_birth')"
                        :isInline="true"
                        :isCompact="true"
                        width="full"
                    >
                        {{ patientBirthDate }}
                    </common-summary-item>
                </template>
            </common-content-spacer>
        </common-summary-item>

        <common-summary-item>
            <referrals-datetime :referral="referral"></referrals-datetime>
        </common-summary-item>

        <common-summary-item
            :label="$t('pages.referrals.show.details.diagnosis')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            {{ referral.diagnosis }}
        </common-summary-item>

        <common-summary-item
            :label="$t('pages.referrals.show.details.insurance')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            {{ referral.insurancePlan?.name }}
        </common-summary-item>

        <common-summary-item
            v-if="referral.insuranceNumber"
            :label="$t('pages.referrals.show.details.insurance_number')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            {{ referral.insuranceNumber }}
        </common-summary-item>

        <common-summary-item
            v-if="referral.insuranceGroupNumber"
            :label="$t('pages.referrals.show.details.insurance_group_number')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            {{ referral.insuranceGroupNumber }}
        </common-summary-item>

        <common-summary-item
            :label="$t('pages.referrals.show.details.insurance_authorization_status')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            {{
                referral.isInsuranceAuthorizationRequired
                    ? $t('pages.referrals.show.details.insurance_authorization_statuses.required')
                    : $t('pages.referrals.show.details.insurance_authorization_statuses.not_required')
            }}
        </common-summary-item>

        <common-summary-item
            v-if="referral.facilityServiceTests && referral.facilityServiceTests.length"
            :label="$t('pages.referrals.show.details.tests')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            <common-badges-list
                :listElements="referral.facilityServiceTests.map((item) => `${item.name} - ${item.group?.name}`)"
            ></common-badges-list>
        </common-summary-item>
        <div v-if="hasSelectedNotes" class="attachments-actions">
            <span class="action-link" @click="downloadSelected">
                {{ downloadText }}
            </span>
            <span class="action-link" @click="printSelected">
                {{ printText }}
            </span>
        </div>
        <common-summary-item
            v-if="referral.referralNotes && referral.referralNotes.length"
            :label="$t('pages.referrals.show.details.attachments_notes', { n: referral.referralNotes.length })"
            :isInline="false"
            :isCompact="false"
            width="full"
        >
            <common-images
                ref="images"
                :images="referral.referralNotes"
                :showMoreOption="true"
                :showCheckBox="true"
                @selection-change="onNotesSelection"
            ></common-images>
        </common-summary-item>

        <common-summary-item
            v-if="referral.consultationReports && referral.consultationReports.length"
            :label="$t('pages.referrals.show.details.attachments_consultation', { n: referral.consultationReports.length })"
            :isInline="false"
            :isCompact="false"
            width="full"
        >
            <common-images
                ref="images"
                :images="referral.consultationReports"
                :showMoreOption="true"
                :showCheckBox="true"
                @selection-change="onConsultationSelection"
            ></common-images>
        </common-summary-item>

        <common-summary-item
            v-if="referral.attachments && referral.attachments.length"
            :label="$t('pages.referrals.show.details.attachments_message', { n: referral.attachments.length })"
            :isInline="false"
            :isCompact="false"
            width="full"
        >
            <common-images
                ref="images"
                :images="referral.attachments"
                :showMoreOption="true"
                :showCheckBox="true"
                @selection-change="onAttachmentsSelection"
            ></common-images>
        </common-summary-item>
        <common-summary-item
            v-if="referral.tests && referral.tests.length"
            :label="$t('pages.referrals.show.details.tests')"
            :isInline="true"
            :isCompact="true"
            width="full"
        >
            <common-badges-list :listElements="referral.tests.map((item) => item.name)"></common-badges-list>
        </common-summary-item>
    </common-summary>
</template>

<script>
    import { isMaskedDateOfBirth } from '~/helpers'
    import ReferralsDatetime from 'components/page-elements/ReferralsDatetime'
    import dayjs from 'dayjs'

    export default {
        components: {
            ReferralsDatetime,
        },

        props: {
            referral: { type: Object, required: true },
        },

        data() {
            return {
                selectedReferralNotes: [],
                selectedConsultationReports: [],
                selectedAttachments: [],
            }
        },
        inject: {
            isReferralPool: {
                default: false,
            },
        },

        computed: {
            allSelectedNotes() {
                return [...this.selectedReferralNotes, ...this.selectedConsultationReports, ...this.selectedAttachments]
            },

            hasSelectedNotes() {
                return this.allSelectedNotes.length > 0
            },
            patientBirthDate() {
                if (isMaskedDateOfBirth(this.referral.patient?.dateOfBirth)) return '—'
                return dayjs(this.referral.patient?.dateOfBirth).format('MM/DD/YYYY')
            },
            downloadText() {
                return this.$t('common.images.download')
            },
            printText() {
                return this.$t('common.images.print')
            },
        },
        methods: {
            downloadSelected() {
                this.allSelectedNotes.forEach((file) => {
                    this.$refs.images.download(file)
                })
            },

            printSelected() {
                this.allSelectedNotes.forEach((file) => {
                    const routeData = this.$router.resolve({ name: 'image.preview', query: { url: file.url } })
                    window.open(routeData.href, '_blank')
                })
            },
            onNotesSelection(images) {
                this.selectedReferralNotes = images
            },
            onConsultationSelection(images) {
                this.selectedConsultationReports = images
            },
            onAttachmentsSelection(images) {
                this.selectedAttachments = images
            },
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    .attachments-actions {
        display: flex;
        justify-content: flex-end;
        gap: 16px;
        width: 100%;
    }

    .action-link {
        cursor: pointer;
        text-decoration: underline;
        font-weight: 500;
        color: $color--accent-1;
        text-transform: uppercase;
        font-size: $font-size--form;
    }
</style>
